import React from 'react';

const IMAGE_GRADIENTS = {
  warmBrown: 'linear-gradient(165deg, #d4b88a, #a98a5f 30%, #5D4A3B 70%, #1C1814 100%)',
  deepBrown: 'linear-gradient(165deg, #3a2e22, #c4a87c)',
  goldRich: 'linear-gradient(165deg, #c4a87c, #5D4A3B)',
  goldSoft: 'linear-gradient(165deg, #d4b88a, #5D4A3B)',
  goldGlow: 'linear-gradient(165deg, #d4b88a, #8a6d4f)',
};

export default function ServiceCard({
  variant = 'large',
  tags = [],
  title,
  description,
  priceFrom,
  priceLabel = 'από',
  priceText,
  href = '#',
  image = 'warmBrown',
  imageUrl,
  imageAlt,
  imageFit = 'cover',
  className = '',
}) {
  const variants = {
    large: {
      bg: 'bg-ivory text-warm-black',
      minHeight: 'min-h-[480px]',
      imageAspect: 'aspect-[16/9]',
      tagBg: 'bg-gold/15 text-brown',
      meta: 'border-brown/15',
      arrow: 'bg-warm-black text-cream',
      layout: 'flex flex-col',
    },
    dark: {
      bg: 'bg-warm-black text-cream',
      minHeight: 'min-h-[480px]',
      imageAspect: 'aspect-[4/5]',
      tagBg: 'bg-gold-light/20 text-gold-light',
      meta: 'border-gold-light/20',
      arrow: 'bg-cream text-warm-black',
      layout: 'flex flex-col',
    },
    full: {
      bg: 'bg-warm-black text-cream',
      minHeight: 'min-h-[360px]',
      imageAspect: 'aspect-square',
      tagBg: 'bg-gold-light/20 text-gold-light',
      meta: 'border-gold-light/20',
      arrow: 'bg-gold-bg text-warm-black',
      layout: 'grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-10 items-center',
    },
    'med-ivory': {
      bg: 'bg-ivory text-warm-black',
      minHeight: 'min-h-[420px]',
      imageAspect: 'aspect-[16/9]',
      tagBg: 'bg-gold/15 text-brown',
      meta: 'border-brown/15',
      arrow: 'bg-warm-black text-cream',
      layout: 'flex flex-col',
    },
    'med-cream': {
      bg: 'bg-cream-soft text-warm-black',
      minHeight: 'min-h-[420px]',
      imageAspect: 'aspect-[16/9]',
      tagBg: 'bg-gold/15 text-brown',
      meta: 'border-brown/15',
      arrow: 'bg-warm-black text-cream',
      layout: 'flex flex-col',
    },
  };
  
  const v = variants[variant] || variants.large;
  const imageStyle = IMAGE_GRADIENTS[image] || IMAGE_GRADIENTS.warmBrown;
  const imageContent = imageUrl ? (
    <img
      src={imageUrl}
      alt={imageAlt || title}
      loading="lazy"
      decoding="async"
      className={`w-full h-full ${imageFit === 'contain' ? 'object-contain' : 'object-cover'} rounded-[18px] transition-transform duration-500 group-hover:scale-[1.03]`}
    />
  ) : null;
  
  const content = (
    <>
      <div className="flex gap-2 flex-wrap mb-3.5">
        {tags.map((tag, i) => (
          <span 
            key={i}
            className={`px-3 py-[5px] rounded-pill text-tiny font-medium tracking-wide ${v.tagBg}`}
          >
            {tag.label}
          </span>
        ))}
      </div>
      
      <h3 className="font-serif text-[clamp(28px,3vw,42px)] leading-[1.05] font-medium mb-3 tracking-tight">
        {title}
      </h3>
      
      <p className="text-[15px] leading-[1.6] opacity-80 mb-[18px]">
        {description}
      </p>
      
      <div className={`flex justify-between items-center mt-auto pt-6 ${v.meta ? `border-t ${v.meta}` : ''}`}>
        <div className="font-serif text-[19px]">
          {priceText ? (
            <span className="text-tiny opacity-70 tracking-wider uppercase">{priceText}</span>
          ) : (
            <>
              <small className="text-tiny opacity-70 tracking-wider uppercase mr-1.5">{priceLabel}</small>
              €{priceFrom}
            </>
          )}
        </div>
        <div className={`w-11 h-11 rounded-full flex items-center justify-center text-lg transition-transform duration-300 group-hover:-rotate-45 ${v.arrow}`}>
          →
        </div>
      </div>
    </>
  );
  
  return (
    <a 
      href={href}
      className={`hover-gpu group block rounded-card-l p-8 transition-[transform,box-shadow] duration-500 cursor-pointer hover:-translate-y-2 hover:shadow-card relative overflow-hidden ${v.bg} ${v.minHeight} ${v.layout} ${className}`}
    >
      {variant === 'full' ? (
        <>
          <div className="flex flex-col">
            {content}
          </div>
          <div className={`${v.imageAspect} rounded-[18px] overflow-hidden`} style={{ background: imageStyle }}>
            {imageContent}
          </div>
        </>
      ) : (
        <>
          <div 
            className={`${v.imageAspect} rounded-[18px] mb-6 overflow-hidden`}
            style={{ background: imageStyle }}
          >
            {imageContent}
          </div>
          {content}
        </>
      )}
    </a>
  );
}